import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { formatDate } from 'ember-intl';
import PlatformBadge from './platform-badge';
import Icon from './icon';
import type { TOC } from '@ember/component/template-only';
import { and, notEq, or } from 'ember-truth-helpers';
import { formatDuration, truncate } from '../utils/format';
import { asString, asDateValue } from '../utils/enriched-data';
import imageFadeIn from '../modifiers/image-fade-in';
import type { ContentSource } from '../schemas/content-source.ts';
import type { UserSourceContentSourceEmbed } from '../schemas/user-source.ts';
import AlertCircleIcon from '~icons/lucide/alert-circle';
import LoaderIcon from '~icons/lucide/loader';
import XIcon from '~icons/lucide/x';
import UndoIcon from '~icons/lucide/undo-2';
import tw from '../helpers/tw';

/** Common source type accepted by the card */
export type SourceCardSource = ContentSource | UserSourceContentSourceEmbed;

interface Signature {
  Args: {
    source: SourceCardSource;
    showCreator?: boolean;
    aspectRatio?: string;
    /** When true, card is visually dimmed */
    dismissed?: boolean | null;
    /** Callback to dismiss — when present, dismiss button is shown */
    onDismiss?: () => void;
    /** Callback to restore — when present, restore button is shown */
    onRestore?: () => void;
    /** Disables action buttons (e.g. while request in flight) */
    disabled?: boolean;
    /** When true, hides the title/creator/badges section below the thumbnail */
    hideTitle?: boolean;
  };
}

function isImageSource(source: SourceCardSource): boolean {
  const s = source as unknown as Record<string, unknown>;
  return s?.['sourceType'] === 'image' || s?.['platform'] === 'image';
}

const PROCESSING_STATES = new Set([
  'pending',
  'downloading',
  'transcribing',
  'analyzing',
  'enriching',
]);

function isProcessingState(state: string | null | undefined): boolean {
  return typeof state === 'string' && PROCESSING_STATES.has(state);
}

function isFailedState(state: string | null | undefined): boolean {
  return state === 'failed' || state === 'not_found';
}

/** Safe access to optional postedAt field */
function postedAt(source: SourceCardSource): string | null {
  return (source as unknown as Record<string, unknown>)['postedAt'] as
    string | null;
}

/** Safe creator display name */
function creatorName(source: SourceCardSource): string | null {
  const c = source.creator;
  if (!c) return null;
  return c.displayName ?? ('username' in c ? c.username : null);
}

/** Whether the card has action buttons */
function hasActions(onDismiss?: () => void, onRestore?: () => void): boolean {
  return !!(onDismiss ?? onRestore);
}

const TYPE_EMOJI: Record<string, string> = {
  movie: '🎬',
  tv_show: '📺',
  book: '📚',
  music: '🎵',
  place: '📍',
  podcast: '🎙️',
  video_game: '🎮',
  event: '🎟️',
  person: '👤',
  brand: '🏷️',
  product: '🛍️',
  recipe: '🧑‍🍳',
};

/** Return sorted type-count badges from the source's itemTypeCounts */
function sortedTypeBadges(
  source: SourceCardSource
): { emoji: string; count: number }[] {
  const counts = (source as unknown as Record<string, unknown>)[
    'itemTypeCounts'
  ] as Record<string, number> | null | undefined;
  if (!counts) return [];
  return Object.entries(counts)
    .filter(([, n]) => n > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([type, n]) => ({ emoji: TYPE_EMOJI[type] ?? '❓', count: n }));
}

const SourceCard: TOC<Signature> = <template>
  <div class={{tw "relative" (if @dismissed "opacity-60")}}>
    <LinkTo
      @route="sources.show"
      @model={{@source.id}}
      class="card bg-base-100 overflow-hidden shadow-md transition-all duration-150 hover:shadow-lg active:scale-[0.97] active:shadow-sm"
    >
      <figure
        class={{tw
          "bg-base-200 relative w-full overflow-hidden rounded-t-2xl"
          @aspectRatio
        }}
      >
        {{#if @source.thumbnailUrl}}
          <img
            {{imageFadeIn}}
            src={{@source.thumbnailUrl}}
            data-fallback-src="/images/placeholders/source.png"
            alt={{or @source.title @source.platform}}
            class="block h-auto w-full opacity-0 transition-opacity duration-300"
            loading="lazy"
          />
        {{else if (isImageSource @source)}}
          <div
            class="text-base-content/20 flex aspect-square w-full items-center justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-12 w-12"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z"
              />
            </svg>
          </div>
        {{else}}
          <div
            class="text-base-content/20 flex aspect-video w-full items-center justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-12 w-12"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        {{/if}}
        {{#if (and @source.duration (notEq (isImageSource @source) true))}}
          <span
            class="absolute right-2 bottom-2 rounded bg-black/70 px-1.5 py-0.5 text-xs text-white"
          >
            {{formatDuration @source.duration}}
          </span>
        {{/if}}
        {{#if (isProcessingState @source.state)}}
          <span
            class="absolute top-2 right-2 flex items-center gap-1 rounded bg-amber-500/90 px-1.5 py-0.5 text-xs text-white"
          >
            <Icon @icon={{LoaderIcon}} class="h-3 w-3 animate-spin" />
            Processing
          </span>
        {{else if (isFailedState @source.state)}}
          <span
            class="absolute top-2 right-2 flex items-center gap-1 rounded bg-red-500/90 px-1.5 py-0.5 text-xs text-white"
          >
            <Icon @icon={{AlertCircleIcon}} class="h-3 w-3" />
            Failed
          </span>
        {{/if}}
        <div class="absolute top-2 left-2">
          <PlatformBadge @platform={{@source.platform}} />
        </div>
      </figure>
      {{#unless @hideTitle}}
        <div class="card-body min-w-0 p-3">
          <h3
            class="line-clamp-2 min-h-10 text-sm leading-tight font-semibold break-all"
          >
            {{or @source.title (truncate (asString @source.url) 60)}}
          </h3>
          <div
            class="text-base-content/60 mt-1 flex items-center justify-between text-xs"
          >
            {{#if (and (notEq @showCreator false) @source.creator)}}
              <span class="truncate">
                {{creatorName @source}}
              </span>
            {{else}}
              <span></span>
            {{/if}}
            {{#if (postedAt @source)}}
              <span class="shrink-0 pl-2">
                {{formatDate (asDateValue (postedAt @source))}}
              </span>
            {{/if}}
          </div>
          {{#let (sortedTypeBadges @source) as |badges|}}
            {{#if badges.length}}
              <div class="mt-1.5 flex flex-wrap gap-1">
                {{#each badges as |badge|}}
                  <span class="badge badge-ghost badge-xs">
                    {{badge.emoji}}
                    {{badge.count}}
                  </span>
                {{/each}}
              </div>
            {{/if}}
          {{/let}}
        </div>
      {{/unless}}
    </LinkTo>

    {{! Owner action buttons — positioned outside LinkTo to avoid nav }}
    {{#if (hasActions @onDismiss @onRestore)}}
      <div class="absolute right-2 bottom-2 z-10 flex gap-1">
        {{#if @dismissed}}
          {{#if @onRestore}}
            <button
              type="button"
              class="btn btn-xs btn-ghost gap-1"
              title="Restore source"
              disabled={{@disabled}}
              {{on "click" @onRestore}}
            >
              <Icon @icon={{UndoIcon}} class="h-3 w-3" />
              Restore
            </button>
          {{/if}}
        {{else}}
          {{#if @onDismiss}}
            <button
              type="button"
              class="btn btn-xs btn-ghost gap-1"
              title="Dismiss source and its items"
              disabled={{@disabled}}
              {{on "click" @onDismiss}}
            >
              <Icon @icon={{XIcon}} class="h-3 w-3" />
              Dismiss
            </button>
          {{/if}}
        {{/if}}
      </div>
    {{/if}}
  </div>
</template>;

export default SourceCard;
